import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SavedSoulBond, CreateSoulBondData, AppSettings } from '@/types/soulBond';

const SERVICE_TAG = '[SOULBONDS]';

interface SoulBondsState {
  soulBonds: SavedSoulBond[];
  appSettings: AppSettings;

  addSoulBond: (data: CreateSoulBondData) => string;
  updateSoulBond: (id: string, updates: Partial<SavedSoulBond>) => void;
  deleteSoulBond: (id: string) => void;
  getSoulBond: (id: string) => SavedSoulBond | undefined;
  markAsUsed: (id: string) => void;
  updateSettings: (settings: Partial<AppSettings>) => void;
}

/**
 * Generate a simple UUID for soul bond IDs
 */
function generateId(): string {
  return `sb-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const initialSettings: AppSettings = {
  preferredCamera: undefined,
  defaultDiceType: 'd20',
  lastActiveSoulBondId: undefined
};

/**
 * Run the rehydration migrations against a partially-typed persisted state.
 * Exported for unit testing — also invoked from `onRehydrateStorage` below.
 *
 * Idempotent: each step is gated on its own `_vNMigrationComplete` flag.
 *
 * Steps:
 *   v3 — relocate `arcaneSettings` to `appSettings`
 *   v4 — translate legacy `aiRecognitionMode` ('fast'|'accurate') to `aiRecognitionModel`
 *   v5 — seed `appSettings.captureMode` to `'photo'` for existing users (TICKET-299)
 */
export function migrateSoulBondsState(
  state: { appSettings?: AppSettings } & Record<string, unknown>
): void {
  // v3 migration: arcaneSettings -> appSettings
  const raw = state as Record<string, unknown>;
  if (!raw._v3MigrationComplete && raw.arcaneSettings && !state.appSettings) {
    console.info(`${SERVICE_TAG} Migrating arcaneSettings -> appSettings`);
    state.appSettings = raw.arcaneSettings as AppSettings;
    delete raw.arcaneSettings;
  }
  raw._v3MigrationComplete = true;

  // v4 migration: aiRecognitionMode -> aiRecognitionModel
  // 'fast' -> 'gpt-4.1-mini', 'accurate' -> 'gpt-5.2'.
  // We do NOT promote 'accurate' to 'claude-opus-4-7' here because auth
  // state is not reliably available at rehydrate time.
  if (!raw._v4MigrationComplete && state.appSettings) {
    const settings = state.appSettings as AppSettings & {
      aiRecognitionMode?: 'fast' | 'accurate';
    };
    if (settings.aiRecognitionMode !== undefined && settings.aiRecognitionModel === undefined) {
      const migrated =
        settings.aiRecognitionMode === 'accurate' ? 'gpt-5.2' : 'gpt-4.1-mini';
      console.info(`${SERVICE_TAG} Migrating aiRecognitionMode -> aiRecognitionModel`, {
        from: settings.aiRecognitionMode,
        to: migrated
      });
      settings.aiRecognitionModel = migrated;
    }
    // Always strip the stale key so it doesn't reappear in subsequent writes.
    if ('aiRecognitionMode' in settings) {
      delete settings.aiRecognitionMode;
    }
  }
  raw._v4MigrationComplete = true;

  // v5 migration (TICKET-299): seed appSettings.captureMode for existing
  // users. Default to 'photo' which matches today's tap-to-capture behaviour.
  if (!raw._v5MigrationComplete && state.appSettings) {
    if (state.appSettings.captureMode === undefined) {
      console.info(`${SERVICE_TAG} Seeding captureMode default`, { value: 'photo' });
      state.appSettings.captureMode = 'photo';
    }
  }
  raw._v5MigrationComplete = true;
}

export const useSoulBondsStore = create<SoulBondsState>()(
  persist(
    (set, get) => ({
      soulBonds: [],
      appSettings: initialSettings,

      addSoulBond: (data) => {
        const id = generateId();
        const now = new Date().toISOString();
        const newSoulBond: SavedSoulBond = {
          ...data,
          id,
          createdAt: now,
          lastUsedAt: now
        };

        console.info(`${SERVICE_TAG} Adding soul bond`, { id, name: data.name });
        set((state) => ({
          soulBonds: [...state.soulBonds, newSoulBond]
        }));

        return id;
      },

      updateSoulBond: (id, updates) => {
        console.info(`${SERVICE_TAG} Updating soul bond`, { id, updates: Object.keys(updates) });
        set((state) => ({
          soulBonds: state.soulBonds.map((sb) =>
            sb.id === id ? { ...sb, ...updates } : sb
          )
        }));
      },

      deleteSoulBond: (id) => {
        const soulBond = get().soulBonds.find((sb) => sb.id === id);
        console.info(`${SERVICE_TAG} Deleting soul bond`, { id, name: soulBond?.name });
        set((state) => ({
          soulBonds: state.soulBonds.filter((sb) => sb.id !== id),
          appSettings: state.appSettings.lastActiveSoulBondId === id
            ? { ...state.appSettings, lastActiveSoulBondId: undefined }
            : state.appSettings
        }));
      },

      getSoulBond: (id) => {
        return get().soulBonds.find((sb) => sb.id === id);
      },

      markAsUsed: (id) => {
        const now = new Date().toISOString();
        console.info(`${SERVICE_TAG} Marking soul bond as used`, { id });
        set((state) => ({
          soulBonds: state.soulBonds.map((sb) =>
            sb.id === id ? { ...sb, lastUsedAt: now } : sb
          ),
          appSettings: { ...state.appSettings, lastActiveSoulBondId: id }
        }));
      },

      updateSettings: (settings) => {
        console.info(`${SERVICE_TAG} Updating settings`, { keys: Object.keys(settings) });
        set((state) => ({
          appSettings: { ...state.appSettings, ...settings }
        }));
      }
    }),
    {
      name: 'dicecapture-soulbonds',
      partialize: (state) => ({
        soulBonds: state.soulBonds,
        appSettings: state.appSettings
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (!state) return;
          migrateSoulBondsState(state as unknown as { appSettings?: AppSettings } & Record<string, unknown>);
        };
      }
    }
  )
);
